import {
  getRecord,
  getRecords,
  insertRecord,
  updateRecord,
} from "@/utils/helpers/data";
import { TABLES } from "@/utils/constants";
import { WeekDay } from "@/utils/types";

export const COLUMNS = {
  ID: "id",
  DENTIST_ID: "dentist_id",
  DAY: "day",
  START_TIME: "start_time",
  END_TIME: "end_time",
  OPEN: "open",
  CREATED_AT: "created_at",
  UPDATED_AT: "updated_at",
};

export interface DentistHour {
  id?: string;
  dentist_id?: string;
  day: WeekDay;
  start_time: string | null;
  end_time: string | null;
  open: boolean;
  created_at?: string;
  updated_at?: string;
}

/**
 * Creates a new dentist hour record.
 * @param dentistHour - The dentist hour data to insert.
 * @returns The created dentist hour or null if insertion failed.
 */
export const createDentistHour = (dentistHour: DentistHour) =>
  insertRecord<DentistHour>(TABLES.DENTIST_HOURS, dentistHour);

/**
 * Updates an existing dentist hour record.
 * @param id - The ID of the dentist hour.
 * @param dentistHour - The updated dentist hour data.
 * @returns The updated dentist hour or null if update failed.
 */
export const updateDentistHour = async ({
  id,
  dentistHour,
}: {
  id: string | null;
  dentistHour: DentistHour;
}): Promise<DentistHour | null> => {
  if (!id) return null;
  return updateRecord<DentistHour>(
    TABLES.DENTIST_HOURS,
    COLUMNS.ID,
    id,
    dentistHour,
  );
};

/**
 * Retrieves a dentist hour by its ID.
 * @param id - The ID of the dentist hour.
 * @returns The dentist hour or null if not found.
 */
export const getDentistHour = (id: string) =>
  getRecord<DentistHour>(TABLES.DENTIST_HOURS, COLUMNS.ID, id);

/**
 * Retrieves all dentist hours associated with a specific dentist ID.
 * @param dentistId - The ID of the dentist.
 * @returns An array of dentist hours.
 */
export const getDentistHoursByDentistId = (dentistId: string) =>
  getRecords<DentistHour>(TABLES.DENTIST_HOURS, COLUMNS.DENTIST_ID, dentistId);
